import {
  Box, Button, DialogActions, DialogContent, DialogTitle, Grid,
  Typography, Avatar, Autocomplete, Stack, Tooltip, Modal, IconButton, TextField, Divider, Chip, MenuItem
} from '@mui/material';
import React, { FC, useEffect, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector, } from "../../store/hooks";
import { StyledInput } from './Dialog.styles';
import { fetchAccountsByName } from "../../store/accountThunk";
import CustomTextField from '../FormFields/CustomTextField';
import Address from './ClientAgent/Address';
import Legal from './ClientAgent/Legal';
import Payment from './ClientAgent/Payment';
import Bank from './ClientAgent/Bank';
import { FormProvider, useForm } from 'react-hook-form';
import { IAccountBasicFormInputs } from "../../utils/account.types";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { ApiStatus } from '../../utils/api.types';
import LoadingButton from '@mui/lab/LoadingButton';
import { addBuyerSellerAccount, updateBuyerSellerAccount } from "../../store/accountThunk";
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import { fetchSuppliers } from '../../store/supplierStore/supplierThunk';
import { fetchCustomers } from '../../store/customerStore/customerThunk';
import { ICustomerDetails } from '../../utils/customer.types';
import { InvoiceFormInputs } from '../../utils/invoice.types';
import { method } from 'lodash';
import { toast } from 'react-toastify';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import HighlightOffTwoToneIcon from '@mui/icons-material/HighlightOffTwoTone';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';

import EmailChip from '../EmailChip/EmailChip';
import { makeStyles } from '@material-ui/core';
import DocAttachInfo from '../DocumentAttachment/DocAttachInfo';
import { buildAuditChanges } from '../../utils/auditChanges';

// Human labels for audited party (Customer/Supplier) fields. Only these keys are
// diffed for the audit trail; keys use the flat form field names.
const PARTY_LABEL_MAP: Record<string, string> = {
  name: 'Name',
  legalName: 'Legal Name',
  website: 'Website',
  contactName: 'Contact Name',
  email: 'Email',
  whatsAppNumber: 'WhatsApp Number',
  dateOfBirth: 'Date of Birth',
  age: 'Age',
  gender: 'Gender',
  billingAddressCity: 'Billing Address — City',
  billingAddressLine1: 'Billing Address — Line 1',
  billingAddressLine2: 'Billing Address — Line 2',
  billingAddressPinCode: 'Billing Address — Pincode',
  shippingAddressCity: 'Shipping Address — City',
  shippingAddressLine1: 'Shipping Address — Line 1',
  shippingAddressLine2: 'Shipping Address — Line 2',
  shippingAddressPinCode: 'Shipping Address — Pincode',
  registrationNo: 'Registration Number',
  taxIdentificationNumber1: 'PAN',
  taxIdentificationNumber2: 'GST',
  // openingBalance{Amount,Type} intentionally NOT audited here — they are tracked
  // separately in the opening-balance history, and the form defaults ('Dr', '')
  // otherwise produce a spurious "changed" on every legacy party's first save.
  accountName: 'Account Name',
  accountNumber: 'Account Number',
  bankName: 'Bank Name',
  branchName: 'Branch Name',
  IFSC: 'IFSC',
};

// Flatten the originally-loaded party record into the same flat field names used
// by the edit form, so buildAuditChanges can diff like-for-like.
const flattenPartyOriginal = (sel: any): Record<string, any> => {
  if (!sel) return {};
  const addresses: any[] = Array.isArray(sel.addresses) ? sel.addresses : [];
  const billing = addresses.find((a: any) => a?.type === 'Billing') || {};
  const shipping = addresses.find((a: any) => a?.type === 'Shipping') || {};
  const bank = (Array.isArray(sel.bankAccounts) && sel.bankAccounts[0]) || {};
  return {
    name: sel.name,
    legalName: sel.legalName,
    website: sel.website,
    contactName: sel.contactName,
    email: sel.email,
    whatsAppNumber: sel.whatsAppNumber,
    dateOfBirth: sel.dateOfBirth ? String(sel.dateOfBirth).slice(0, 10) : undefined,
    age: sel.age,
    gender: sel.gender,
    billingAddressCity: billing.city,
    billingAddressLine1: billing.line1,
    billingAddressLine2: billing.line2,
    billingAddressPinCode: billing.code,
    billingAddressState: billing.state?._id || billing.state,
    billingAddressCountry: billing.country?._id || billing.country,
    shippingAddressCity: shipping.city,
    shippingAddressLine1: shipping.line1,
    shippingAddressLine2: shipping.line2,
    shippingAddressPinCode: shipping.code,
    shippingAddressState: shipping.state?._id || shipping.state,
    shippingAddressCountry: shipping.country?._id || shipping.country,
    registrationNo: sel.registrationNo,
    taxIdentificationNumber1: sel.taxIdentificationNumber1,
    taxIdentificationNumber2: sel.taxIdentificationNumber2,
    accountName: bank.accountName,
    accountNumber: bank.accountNumber,
    bankName: bank.bankName,
    branchName: bank.branchName,
    IFSC: bank.IFSC,
  };
};

interface DialogClientFormProps {
  title?: string;
  open: boolean;
  onClose: (refresh: any) => void;
  onCancel: () => void;
  isClientEdit?: boolean;
  selectedData?: ICustomerDetails;
  /** Called with the saved name after a successful create / update. */
  onSaved?: (name: string) => void;
}

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  transition: '0ms',
  width: 1000,
  borderRadius: 2,
  bgcolor: 'background.paper',
  border: 'none',
  boxShadow: 24,
};

const useStyles = makeStyles({
  paper: {
    border: "1.5px solid #e9ecf1",
    boxShadow: 'none',
    borderRadius: 7
  },
  toggleButtonGroup: {
    width: '100%',
  },
  toggleButton: {
    width: 15,
    height: 15,
    marginBottom: 2
  },

});

const DialogClientUpdateForm: FC<DialogClientFormProps> = ({ title, onClose, onCancel, open, isClientEdit, selectedData, onSaved}) => {
  // Schema to validate account form.
  const schema = yup.object().shape({
    name: yup.string().required("Name is required."),
    legalName: yup.string().required("Legal Name is required."),
    billingAddressCountry: yup.string().required("Billing Address Country is required."),
    billingAddressCity: yup.string().required("Billing Address City is required."),
    shippingAddressCountry: yup.string().required("Shipping Address Country is required."),
    shippingAddressCity: yup.string().required("Shipping Address City is required."),
    // registrationNo: yup.string().required("Registration is required."),
    // countryOfRegistration: yup.string().required("Country of registration is required."),
    // stateOfRegistration: yup.string().required("State of registration is required."),
    // taxIdentificationNumber1: yup.string().required("Tax Identification #1 (PAN for India) is required."),
    // taxIdentificationNumber2: yup.string().required("Tax Identification #2 (GST for India) is required."),
    // upiId: (title === 'Supplier') ? yup.string().required("UPI ID is required.") : yup.string().notRequired(),
    // gPayPhone: (title === 'Supplier') ? yup.string().required("GPay number is required.") : yup.string().notRequired(),
    // bankName: (title === 'Supplier') ? yup.string().required("Bank name is required.") : yup.string().notRequired(),
    // branchName: (title === 'Supplier') ? yup.string().required("Branch name is required.") : yup.string().notRequired(),
    // IFSC: (title === 'Supplier') ? yup.string().required("IFSC is required.") : yup.string().notRequired(),
    currency: (title === 'Supplier') ? yup.string().required("Currency is required.") : yup.string().notRequired(),
    // accountNumber: (title === 'Supplier') ? yup.string().required("Account number is required.") : yup.string().notRequired(),
    // accountName: (title === 'Supplier') ? yup.string().required("Account name is required.") : yup.string().notRequired()
  });

  const dispatch = useAppDispatch();
  const classes = useStyles();
  const [image, setImage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [options, setOptions] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>();
  const [newOption, setNewOption] = useState("");
  // MUI Select needs its own value; register() alone won't display it.
  const [gender, setGender] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [saveButton, setSaveButton] = useState('Select');
  const { selectedAccount } = useAppSelector(state => state.auth);
  const elmRef = useRef<any>(null);

  const [mailerList, setMailerList] = useState<any>();

  // attachments
  const { docs } = useAppSelector(state => state.docAttach);
  const [docData, setDocData] = useState<any[]>([]);
  const [fetchDocs, setFetchDocs] = useState('');
  const [refreshDocs, setRefreshDocs] = useState(false);
  const [docDeleteId, setDocDeleteId] = useState("");

  //form
  const methods = useForm<IAccountBasicFormInputs>({ mode: 'onTouched', resolver: yupResolver(schema) });
  const formRef = useRef<any>();
  const { status } = useAppSelector(state => state.account);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Method to set preview of selected picture.
  const handleSelectFile = (event: any) => {
    const target = event.target as HTMLInputElement;

    if (target.files && target.files[0]) {
      setSelectedFile(target.files[0]);
      setImage(URL.createObjectURL(target.files[0]));
    }
  };
  const onChangeHandler = (event: any, value: any) => {
    setNewOption('');
    setIsLoading(true);
    setSelected(value);
  }

  const onInputChangeHandler = async (event: React.SyntheticEvent, value: string) => {
    setSelected('');
    setNewOption('');
    await dispatch(fetchAccountsByName(value)).unwrap().then((data) => {
      if (data) {
        const d = data.accounts.filter(function (item) {
          return item._id !== selectedAccount._id;
        });

        setOptions(d);
      }
    });
  };

  const onAddNew = () => {
    if (elmRef) {
      const ele = elmRef?.current.getElementsByClassName('MuiAutocomplete-clearIndicator')[0];
      if (ele) ele.click();
    }
    setSelected('')
    setSaveButton('Create')
    setNewOption('new');
  };

  useEffect(() => {
    console.log("useEffect called 11111");
    console.log(isClientEdit);
    console.log(selectedData);
    if (isClientEdit) {
      console.log("IsEditing called");
      setSelected(selectedData);
      setSaveButton('Update')
      setNewOption('');
    } else {
      setSelected('')
      setSaveButton('Create')
      setNewOption('new');
    }
  }, [selectedData]);

  useEffect(() => {
    methods.reset();
    console.log(selected)
    if (selected) {
      methods.setValue("name", selected.name);
      methods.setValue("legalName", selected.legalName);
      methods.setValue("website", selected.website);
      methods.setValue("contactName", selected.contactName);
      methods.setValue("email", selected.email);
      methods.setValue("whatsAppNumber", selected.whatsAppNumber);
      if (title == 'Buyer') {
        // <input type="date"> needs a YYYY-MM-DD value.
        const dob = (selected as any).dateOfBirth ? String((selected as any).dateOfBirth).slice(0, 10) : '';
        methods.setValue("dateOfBirth", dob);
        // A stored DOB always wins — a saved age goes stale as time passes.
        methods.setValue("age", calculateAge(dob) || ((selected as any).age ?? ''));
        methods.setValue("gender", (selected as any).gender || '');
        setGender((selected as any).gender || '');
      }
      setIsLoading(false);
    } else if (newOption) {
      setGender('');
      setIsLoading(false);
    }
  }, [selected, newOption]);

  const getFormData = (object: any) => {
    const formData = new FormData();
    // FormData would send an unset value as the text "undefined".
    Object.keys(object).forEach(key => {
      formData.append(key, object[key] ?? '')
    });
    console.log(formData);

    // Object.keys(object).forEach(key => formData.append(key, object[key]));
    return formData;
  };

  const createOrUpdateClient = async (e: any) => {
    console.log("createOrUpdateClient Called");
    if (selectedFile) {
      e.append('logo', selectedFile);
    }
    e.delete('email');
    e.append('email', JSON.stringify(mailerList));
    let resData: any = null;

    if (isClientEdit) {
      resData = await dispatch(updateBuyerSellerAccount(
        {
          customerPayload: e, 
          customerId: selected._id
        }
      ));
    } else {
      resData = await dispatch(addBuyerSellerAccount(e));
    }

    // if (resData.payload) {
    //   // toast.success("Customer Updated Successfully");
    //   // setSelected(resData.payload.fullAccountDetails);
    //   // selectedData(resData.payload.fullAccountDetails);
    // }
    if (!resData.error) {
      isClientEdit = false;
      onSaved?.(String(e.get('name') || ''));
      methods.reset();
      onClose(true);
      setDocData([]);
      methods.reset();
    }
  };

  const selectedEmailDataHandler = (data: any) => {
    console.log("====> selectedEmailDataHandler called from updateClientForm  ====> ");
    setMailerList(data);
    console.log(mailerList);
  };

  // Completed years from a YYYY-MM-DD string; '' when it isn't a usable past date.
  const calculateAge = (value: string): number | '' => {
    if (!value) return '';
    const dob = new Date(value);
    if (isNaN(dob.getTime())) return '';
    const today = new Date();
    let years = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) years--;
    return years >= 0 ? years : '';
  };

  // DOB drives age; age stays editable for parties with no DOB.
  const handleDobChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    methods.setValue('dateOfBirth', value);
    const years = calculateAge(value);
    if (years !== '') methods.setValue('age', years);
  };

  
  const onSubmit123 = (e: any) => {
    // e.preventDefault();
    console.log("onSubmit Called");
    const data = methods.getValues();
    console.log(data);
    data.buyerSellerAgent = title;
    data.email = mailerList;
    console.log(data);
    // if (selectedFile) {
    //   console.log(selectedFile);
    //   data.logo = selectedFile;
    // }

    console.log(docData);
    const docsIds = docData.map(({ _id }) => _id);
    console.log(docsIds);
    data.attachments = docsIds;

    delete e.search;
    e.buyerSellerAgent = title;

    let form = getFormData(data);

    // On edit, attach the real user-made change list for the audit trail. Compare
    // the originally-loaded party (flattened to the form's field names) against the
    // submitted values so the backend audits only genuine edits. Shared by both
    // Customer/Buyer and Supplier edit flows (this form serves both). Sent as a
    // JSON string on the FormData payload.
    if (isClientEdit) {
      const orig = flattenPartyOriginal(selected);
      // Guard email: it's set asynchronously from EmailChip; if it hasn't populated
      // yet, fall back to the original so we don't report a phantom "Email changed".
      const auditData: any = { ...data };
      if (auditData.email === undefined || auditData.email === null) {
        auditData.email = (selected as any)?.email;
      }
      const changes = buildAuditChanges(orig, auditData, PARTY_LABEL_MAP);

      // State / Country are stored as ObjectIds (no readable name at this layer), so
      // instead of dumping ids we just record that the field was changed.
      const stateFields: Array<[string, string]> = [
        ['billingAddressState', 'Billing Address — State'],
        ['shippingAddressState', 'Shipping Address — State'],
        ['billingAddressCountry', 'Billing Address — Country'],
        ['shippingAddressCountry', 'Shipping Address — Country'],
      ];
      for (const [key, label] of stateFields) {
        const ov = orig[key] ? String(orig[key]) : '';
        const nv = (data as any)[key] ? String((data as any)[key]) : '';
        if (ov !== nv && nv) {
          changes.push({ field: label, oldValue: 'previous', newValue: 'changed' });
        }
      }

      form.append('_auditChanges', JSON.stringify(changes));
    }

    console.log(form.get('logo'))
    console.log(form.get('email'))
    createOrUpdateClient(form);
  };

  useEffect(() => {
    if (status === ApiStatus.SUCCESS && formSubmitted) {
      console.log("in if useeffect called for status,formSubmitted  ", status, formSubmitted);
      onClose(false);
      setFormSubmitted(false);
      setDocData([]);
    }
  }, [status, formSubmitted, onClose]);

  const closeDocAttach = (isSaved: boolean) => {
    console.log("called closeDocAttach -----");
    if (isSaved) {
      setRefreshDocs(true);
      setFetchDocs(Math.random().toString());
    }
  }
  const docDelete = (id: any) => {
    setDocDeleteId(id);
  }

  useEffect(() => {
    if (docDeleteId) {
      let docs = docData.filter(doc => doc._id !== docDeleteId);
      setDocData([...docs]);
      setDocDeleteId('');
    }
  }, [docDeleteId])
  
  useEffect(() => {
    if (docs.length > 0 && fetchDocs && refreshDocs) {
      let len = docs.length;
      setDocData(d => [...d, docs[len - 1]]);
    }
  }, [docs, fetchDocs, refreshDocs])

  return (
    // style={{width: "100%", minWidth:"100%"}}
    <div className="drawer-form-container" style={{zIndex: -10}}>
      <Modal
          open={open}
          onClose={(event : any, reason:any) => {
              if (reason !== 'backdropClick') {
                onClose(false);
              }
          }}
      >
      <FormProvider {...methods}>
        <Box sx={style} component="form" onSubmit={methods.handleSubmit(data => onSubmit123(data))} ref={formRef} noValidate>
            <Grid container>
                <Grid {...{ xs: 12, sm: 8, md: 4, lg: 12, p:2, pl:2, bgcolor:'#e9ecf1' }} style={{borderTopLeftRadius:8,borderTopRightRadius:8,height:50, borderBottom: '1px solid #5f9ea073'}} >
                <div style={{float:'right',marginTop:-7,marginRight:-5}}>

                  <LoadingButton
                    type="submit"
                    name={'submit'}
                    // disabled={!methods.formState.isValid}
                    loading={status === ApiStatus.LOADING}
                    style={{color:'#488dff',backgroundColor:'#fff',border:'1px solid #488dff', marginRight:10 }}
                    variant="contained"
                    size='small'> {saveButton} 
                  </LoadingButton> 
                  <Button
                    style={{color:'#488dff',backgroundColor:'#fff',border:'1px solid #488dff', marginRight:10 }}
                    variant="contained"
                    size='small'
                    onClick={() => {
                      methods.reset();
                      onCancel();
                      onClose(false);
                    }}>
                    Cancel
                  </Button>
                </div>
                <h3  style={{marginTop:-2}}>{isClientEdit ? `Update` : `Create`} {title}</h3>
                
                </Grid>
                
            </Grid>
            <Grid container spacing={2} rowSpacing={1} p={2} height={'90vh'} overflow={'auto'} style={{marginTop: 0}}>

              {/* <Grid item xs={11} >
              <h5 className='c-label2'>{title}</h5>
                  <Autocomplete
                    ref={elmRef}
                    id={`search ${title}`}
                    disablePortal
                    onChange={onChangeHandler}
                    onInputChange={onInputChangeHandler}
                    options={options}
                    renderOption={handleRenderOption}
                    classes={{ paper: classes.paper }}
                    sx={{ width: '100%'}}
                    getOptionLabel={option => option.name}
                    renderInput={(params) => <TextField {...params}
                    placeholder={`Search ${title}`}
                      required 
                      className='c-text-field' 
                      variant="standard" 
                      size='small'
                      InputProps={{...params.InputProps, disableUnderline: true}}
                    />}
                  />
              </Grid>

              <Grid item xs={1} >
              <Button variant='contained' size='small' onClick={onAddNew}
              style={{ marginTop: '18px', color:'#488dff',backgroundColor:'#fff',border:'1px solid #488dff' }}>Add</Button>
              </Grid>

              <Grid item xs={12} mb={1}>
                <Divider/>
              </Grid> */}

              {isLoading ? <div></div> : (selected || (newOption)) && (
                <>
                  <Grid item xs={2} >
                        <Avatar
                          alt={"Picture"}
                          imgProps={{ referrerPolicy: "no-referrer" }}
                          src={(selected && selected.logo) ? selected.logo : image}
                          style={{
                            width: "120px",
                            height: "120px",
                            objectFit: "contain",
                            marginBottom:10,
                            border:'1px dashed #9da7b7',
                            marginLeft:15
                          }}
                          variant='rounded'
                          sizes={"lg"}
                        />
                      {!selected && <label htmlFor="logo">
                        <StyledInput
                          accept="image/*"
                          id="logo"
                          type="file"
                          onChange={handleSelectFile}
                        />
                        <Button
                        style={{marginLeft:15, width:'120px', color:'#488dff',backgroundColor:'#fff',border:'1px dashed #488dff' }}
                          variant="contained"
                          component="span"
                          size='small'
                        >
                          Choose Logo
                        </Button>
                      </label>}
                  </Grid>
                  <Grid item xs={10}>
                  <Grid container spacing={2} rowSpacing={1}>

                  <Grid item xs={7.2} >
                  <h5 className='c-label'>Name*</h5>
                  <TextField
                    className='c-text-field' 
                    {...methods.register('name')}
                    variant="standard" 
                    sx={{width:'100%'}}
                    size='small'
                    InputProps={{
                      disableUnderline: true,
                    }}/>
                    <p style={{color: "#c33b3b"}}>{methods.formState.errors.name?.message ?? ""}</p>
                  </Grid>
                  <Grid item xs={4.8}>
                  <h5 className='c-label'>Legal Name *</h5>
                    <TextField
                    placeholder='legalName' 
                    required 
                    className='c-text-field' 
                    {...methods.register('legalName')}
                    variant="standard" 
                    sx={{width:'100%'}}
                    size='small'
                    InputProps={{
                      disableUnderline: true,
                      // readOnly: (selected) ? true : false
                    }}/> 
                    <p style={{color: "#c33b3b"}}>{methods.formState.errors.legalName?.message ?? ""}</p>
                  </Grid>
                  <Grid item xs={7.2}>
                  <h5 className='c-label'>Website</h5>
                  <TextField
                    placeholder='website' 
                    className='c-text-field' 
                    {...methods.register('website')}
                    variant="standard" 
                    sx={{width:'100%'}}
                    size='small'
                    InputProps={{
                      disableUnderline: true,
                    }}/>
                    <p style={{color: "#c33b3b"}}>{methods.formState.errors.website?.message ?? ""}</p>
                    </Grid>

                  <Grid item xs={4.8}>
                  <h5 className='c-label'>Contact Name</h5>
                  <TextField
                    placeholder='Contact Name' 
                    className='c-text-field'
                    {...methods.register('contactName')}
                    variant="standard" 
                    sx={{width:'100%'}}
                    size='small'
                    InputProps={{
                      disableUnderline: true,
                    }}/>
                    <p style={{color: "#c33b3b"}}>{methods.formState.errors.contactName?.message ?? ""}</p>
                  </Grid>
                  <Grid item xs={7.2}>
                  {/* <h5 className='c-label'>website</h5>
                    <TextField
                    placeholder='website' 
                    className='c-text-field' 
                    name="website"
                    variant="standard" 
                    sx={{width:'100%'}}
                    size='small'
                    InputProps={{
                      disableUnderline: true,
                    }}/> */}
                    {/* <CustomTextField label="Email" name="email" variant="standard" InputProps={{ readOnly: (selected) ? true : false }} /> */}
                    <EmailChip buyerEmail={selected.email} methods={methods} selectedEmailData={selectedEmailDataHandler} isEdit={true}/>
                  </Grid>
                  <Grid item xs={4.8}>
                  <h5 className='c-label'>WhatsApp Number</h5>
                  <TextField
                    placeholder='WhatsApp Number' 
                    type='number'
                    className='c-text-field'
                    {...methods.register('whatsAppNumber')}
                    variant="standard" 
                    sx={{width:'100%'}}
                    size='small'
                    InputProps={{
                      disableUnderline: true,
                    }}/>
                    <p style={{color: "#c33b3b"}}>{methods.formState.errors.whatsAppNumber?.message ?? ""}</p>
                  </Grid>
                  {title == 'Buyer' && <>
                  <Grid item xs={4}>
                  <h5 className='c-label'>Date of Birth</h5>
                  <TextField
                    type='date'
                    className='c-text-field'
                    {...methods.register('dateOfBirth')}
                    onChange={handleDobChange}
                    variant="standard"
                    sx={{width:'100%'}}
                    size='small'
                    InputProps={{
                      disableUnderline: true,
                    }}/>
                  </Grid>
                  <Grid item xs={4}>
                  <h5 className='c-label'>Age</h5>
                  <TextField
                    placeholder='Age'
                    type='number'
                    className='c-text-field'
                    {...methods.register('age')}
                    variant="standard"
                    sx={{width:'100%'}}
                    size='small'
                    InputProps={{
                      disableUnderline: true,
                    }}/>
                  </Grid>
                  <Grid item xs={4}>
                  <h5 className='c-label'>Gender</h5>
                  <TextField
                    select
                    className='c-text-field'
                    {...methods.register('gender')}
                    value={gender}
                    onChange={(event) => {
                      setGender(event.target.value);
                      methods.setValue('gender', event.target.value);
                    }}
                    variant="standard"
                    sx={{width:'100%'}}
                    size='small'
                    InputProps={{
                      disableUnderline: true,
                    }}>
                    <MenuItem value=''>Select Gender</MenuItem>
                    <MenuItem value='Male'>Male</MenuItem>
                    <MenuItem value='Female'>Female</MenuItem>
                    <MenuItem value='Other'>Other</MenuItem>
                  </TextField>
                  </Grid>
                  </>}
                  </Grid>
                  </Grid>
                  <Address selected={selected} methods={methods} isEdit={isClientEdit}/>
                  {title == 'Supplier' &&
                    <>
                      <Grid item xs={6} mt={1}>
                        <Divider style={{marginRight:-8}}>
                          <Chip label="UPI Details " size="small" />
                        </Divider>
                      </Grid>
                      <Grid item xs={6} mt={1}>
                        <Divider style={{marginLeft:-8}}>
                          <Chip label="Bank Details " size="small" />
                        </Divider>
                      </Grid>
                      <Grid item xs={6.1} pr={2}>
                        <Grid container spacing={2} rowSpacing={1} pt={-2}>
                          {/* <Payment selected={selected} methods={methods} isEdit={isClientEdit} /> */}
                        </Grid>
                      </Grid>
                      <Grid item xs={5.9} style={{borderLeft:'1px solid rgba(0, 0, 0, 0.12)'}}>
                        <Grid container spacing={2} rowSpacing={1} >
                          <Bank selected={selected} methods={methods} isEdit={isClientEdit}/>
                        </Grid>
                      </Grid>
                    </>
                  }

                  <Legal selected={selected} methods={methods} isEdit={isClientEdit} />
                  {/* <Grid item xs={12} mt={1}>
                    <Divider style={{marginRight:-8}}>
                      <Chip label="Upload Documents" size="small" />
                    </Divider>
                    <div style={{ height: "85vh", width: "100%", marginTop: '0px', overflow:'auto', zIndex: 999 }}>
                    {docData && <DocAttachInfo docList={docData} onDelete={docDelete} onClose={closeDocAttach} />}
                    </div>

                  </Grid> */}
                </>
              )}
            </Grid>
            {/* <Grid container gap='0' sx={{ mr: '32px', pt: '10px' }}>
            <Grid item xs={4} display="flex" justifyContent="flex-end" >
                <Button variant='outlined'
                  style={{ marginRight: '10px' }}
                  onClick={() => {
                    methods.reset();
                    onCancel();
                    onClose(false);
                  }}>Cancel</Button>
                {selected ?
                  <Button variant='contained'
                    style={{ marginRight: '10px' }}
                    onClick={() => {
                      methods.reset();
                      selectedData(selected);
                      onClose(false);
                    }}>Select</Button> :
                  <LoadingButton
                    type="button"
                    onClick={onSubmit}
                    // disabled={!methods.formState.isValid}
                    loading={status === ApiStatus.LOADING}
                    variant="contained"
                  >
                    {saveButton}
                  </LoadingButton>}
              </Grid>
            </Grid> */}
        </Box>
      </FormProvider>
      </Modal>
    </div >
    // <div className="drawer-form-container">
    //   <FormProvider {...methods}>
    //     <Box
    //       sx={{ display: "flex", flexDirection: "column", height: "100vh", padding: "16px 0 16px 16px" }}
    //       component="form"
    //       // onSubmit={methods.handleSubmit(onSubmit)}
    //       onSubmit={(e:any) => {e.preventDeafult();methods.handleSubmit(onSubmit);}}
    //       noValidate>
    //       <DialogTitle sx={{ height: "0px", mt: 1, mb: 3, justifyContent: "space-between" }}>
    //         <Box >
    //           <Typography variant="h5">{title}</Typography>
    //         </Box>
    //       </DialogTitle>

    //       <DialogContent>
    //         <Grid container justifyContent="center" spacing={2} rowSpacing={-2}>

    //           {/* <Typography> some text : {isClientEdit}</Typography> */}
    //           {isLoading ? <div></div> : (isClientEdit || (newOption)) && (
    //             <>
    //               <Grid item xs={12} md={12} sm={12} lg={12}>
    //                 <div style={{ position: "relative", display: "flex", flexDirection: "column", justifyItems: "left", alignItems: "left" }}>
    //                   <div className="file-name" style={{ marginBottom: "10px" }}>
    //                     <Avatar
    //                       alt={"Picture"}
    //                       imgProps={{ referrerPolicy: "no-referrer" }}
    //                       src={(selected && selected.logo) ? selected.logo : image}
    //                       // src={(image) ? image : selected.logo}
    //                       style={{
    //                         width: "100px",
    //                         height: "100px",
    //                         objectFit: "contain",
    //                       }}
    //                       variant='square'
    //                       sizes={"lg"}
    //                     />
    //                   </div>
    //                   {<label htmlFor="logo">
    //                     <StyledInput
    //                       accept="image/*"
    //                       id="logo"
    //                       type="file"
    //                       onChange={handleSelectFile}
    //                     />
    //                     <Button
    //                       variant="outlined"
    //                       className="btn-choose"
    //                       component="span"
    //                     >
    //                       Choose Logo
    //                     </Button>
    //                   </label>}
    //                 </div>
    //               </Grid>
    //               <Grid item xs={12} md={12} sm={12} lg={12}>
    //               {/* InputProps={{ readOnly: (isClientEdit) ? true : false }} */}
    //                 <CustomTextField label="Brand Name" name="name" required variant="standard" />
    //               </Grid>
    //               <Grid item xs={12} md={12} sm={12} lg={12}>
    //                 <CustomTextField label="Legal Name" name="legalName" required variant="standard" />
    //               </Grid>
    //               <Grid item xs={12} md={12} sm={12} lg={12}>
    //                 <CustomTextField label="Website" name="website" variant="standard" />
    //               </Grid>

    //               <Grid sx={{ mt: 5 }} item xs={12} md={12} sm={12} lg={12}>
    //                 <Typography variant="body1">{'Contact Details'}</Typography>
    //                 <CustomTextField label="Contact Name" name="contactName" variant="standard" />
    //               </Grid>
    //               {/* <Grid item xs={12} md={12} sm={12} lg={12}>
    //                 <CustomTextField label="Email" name="email" variant="standard" />
    //               </Grid> */}
                  
    //               <EmailChip buyerEmail={selected.email} methods={methods} selectedEmailData={selectedEmailDataHandler} isEdit={isClientEdit}/>
                  
    //               <Grid item xs={12} md={12} sm={12} lg={12}>
    //                 <CustomTextField label="WhatsApp Number" name="whatsAppNumber" variant="standard" />
    //               </Grid>

    //               <Address selected={selected} methods={methods} isEdit={isClientEdit}/>
    //               <Legal selected={selected} methods={methods} isEdit={isClientEdit}/>
    //               {title === 'Supplier' && <Payment selected={selected} methods={methods} />}
    //               {title === 'Supplier' && <Bank selected={selected} methods={methods} />}
    //             </>
    //           )}
    //         </Grid>
    //       </DialogContent>

    //       <DialogActions sx={{ height: "40px", mb: 2 }}>
    //         <Grid container gap='0' sx={{ mr: '32px', pt: '10px' }}>
    //           <Grid item xs={12} md={12} sm={12} lg={12} display="flex" justifyContent="flex-end" >
    //             <Button variant='outlined'
    //               style={{ marginRight: '10px' }}
    //               onClick={() => {
    //                 methods.reset();
    //                 onCancel();
    //                 onClose(false);
    //               }}>Cancel</Button>
    //             {<LoadingButton
    //                 type="button"
    //                 onClick={onSubmit}
    //                 // disabled={!methods.formState.isValid}
    //                 loading={status === ApiStatus.LOADING}
    //                 variant="contained"
    //               >
    //                 {saveButton}
    //               </LoadingButton>}
    //           </Grid>
    //         </Grid>
    //       </DialogActions>
    //     </Box>
    //   </FormProvider>
    // </div >
  );
};

export default DialogClientUpdateForm;
